/** One Web Audio clock for synchronized directional tracks and independent mute gains. */
export class DiagnosticAudioClock {
  private sources: AudioBufferSourceNode[] = [];
  private gains: GainNode[];
  private offset = 0;
  private startedAt = 0;
  private running = false;
  private rate = 1;
  private closed = false;
  readonly duration: number;
  readonly context: AudioContext;
  readonly buffers: AudioBuffer[];

  constructor(context: AudioContext, buffers: AudioBuffer[]) {
    this.context = context;
    this.buffers = buffers;
    this.duration = Math.max(...buffers.map(buffer => buffer.duration), 0);
    this.gains = buffers.map(() => {
      const gain = context.createGain();
      gain.connect(context.destination);
      return gain;
    });
  }

  /** Read elapsed audio time, independent of animation frames and browser timer throttling. */
  get position(): number {
    return Math.max(0, Math.min(this.duration, this.offset + (this.running ? Math.max(0, this.context.currentTime - this.startedAt) * this.rate : 0)));
  }

  /** Start every track at the same context time and offset; a shorter track ends naturally. */
  async play(): Promise<void> {
    await this.context.resume();
    if (this.running) return;
    if (this.offset >= this.duration) this.offset = 0;
    this.startedAt = this.context.currentTime + 0.02;
    this.sources = this.buffers.map((buffer, index) => {
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = this.rate;
      source.connect(this.gains[index]);
      // Starting even exhausted buffers lets stop()/disconnect() remain uniform.
      source.start(this.startedAt, Math.min(this.offset, buffer.duration));
      return source;
    });
    this.running = true;
  }

  /** Stop sources and retain the common cursor so the next play resumes both together. */
  pause(): void {
    this.offset = Math.max(0, this.position);
    this.running = false;
    this.sources.forEach(source => { source.stop(); source.disconnect(); });
    this.sources = [];
  }

  /** Recreate sources at a shared seek boundary, preserving playback state. */
  async seek(seconds: number): Promise<void> {
    const resume = this.running;
    this.pause();
    this.offset = Math.max(0, Math.min(this.duration, seconds));
    if (resume) await this.play();
  }

  /** Apply one playback speed to both tracks without changing their relative timeline. */
  async setRate(rate: number): Promise<void> {
    const resume = this.running;
    this.pause();
    this.rate = rate;
    if (resume) await this.play();
  }

  mute(index: number, muted: boolean): void {
    this.gains[index].gain.value = muted ? 0 : 1;
  }

  /** Release sources, gain nodes and audio hardware when the drawer closes. */
  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.pause();
    this.gains.forEach(gain => gain.disconnect());
    if (this.context.state !== 'closed') await this.context.close();
  }
}

/** Inspect RIFF PCM headers before decode to bound resampled Float32 memory use. */
export function diagnosticDecodedBytes(wav: ArrayBuffer, outputSampleRate: number): number {
  const view = new DataView(wav);
  const text = (at: number) => String.fromCharCode(...new Uint8Array(wav, at, 4));
  if (wav.byteLength < 44 || text(0) !== 'RIFF' || text(8) !== 'WAVE') throw new Error('invalid_wav');
  let byteRate = 0;
  for (let offset = 12; offset + 8 <= wav.byteLength;) {
    const size = view.getUint32(offset + 4, true);
    if (offset + 8 + size > wav.byteLength) throw new Error('truncated_wav');
    if (text(offset) === 'fmt ') {
      if (size < 16 || view.getUint16(offset + 8, true) !== 1 || view.getUint16(offset + 10, true) !== 1 || view.getUint16(offset + 22, true) !== 16) throw new Error('unsupported_wav');
      byteRate = view.getUint32(offset + 16, true);
    }
    if (text(offset) === 'data' && byteRate > 0) return Math.ceil(size / byteRate * outputSampleRate) * 4;
    offset += 8 + size + (size % 2);
  }
  throw new Error('invalid_wav');
}

/** Build bounded peak bars without trimming silence or modifying decoded playback buffers. */
export function diagnosticPeaks(buffer: AudioBuffer, count = 200): number[] {
  const data = buffer.getChannelData(0);
  const stride = Math.max(1, Math.ceil(data.length / count));
  return Array.from({ length: count }, (_, bar) => {
    let peak = 0;
    for (let index = bar * stride; index < Math.min(data.length, (bar + 1) * stride); index += 1) {
      peak = Math.max(peak, Math.abs(data[index]));
    }
    return peak;
  });
}
